import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import Chip from '@mui/material/Chip';
import { MultiStorePickerProps, StoreOption } from './types';

const MultiStorePicker: React.FC<MultiStorePickerProps> = ({
  stores,
  value,
  onChange,
  allStoresLabel = '',
  label = 'Store',
  placeholder = 'Search',
  disabled = false,
  size = 'small',
  hideAllStoresOption = false,
  maxSelections,
  selectorWidth,
  allStoresGap = 4,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const sortStoreOptionsAlphanumeric = (options: StoreOption[]): StoreOption[] => {
    const numeric: StoreOption[] = [];
    const alpha: StoreOption[] = [];
    options.forEach(option => {
      const match = option.storeName.match(/^\d+/);
      if (match) {
        option._numKey = parseInt(match[0], 10);
        numeric.push(option);
      } else {
        alpha.push(option);
      }
    });
    numeric.sort((a, b) =>
      (a._numKey ?? 0) === (b._numKey ?? 0)
        ? a.storeName.localeCompare(b.storeName)
        : (a._numKey ?? 0) - (b._numKey ?? 0),
    );
    alpha.sort((a, b) => a.storeName.localeCompare(b.storeName));
    return [...numeric, ...alpha];
  };

  const storeOptions = useMemo<StoreOption[]>(() => sortStoreOptionsAlphanumeric(
    (stores || [])
      .map(store => ({ storeName: store.storeName ?? '', storeNumber: store.storeNumber ?? '' }))
      .filter(store => store.storeName),
  ), [stores]);

  const storeNumbers = useMemo(() => storeOptions.map(store => store.storeNumber), [storeOptions]);
  const allStoresSelected = !hideAllStoresOption && value.mode === 'all';
  const selectedStoreChips: StoreOption[] = allStoresSelected
    ? storeOptions
    : value.mode === 'specific'
      ? sortStoreOptionsAlphanumeric(value.storeNumbers.map(storeNumber => {
        const store = stores?.find(candidate => candidate.storeNumber === storeNumber);
        return { storeName: store?.storeName || storeNumber, storeNumber };
      }))
      : [];

  const handleStoreAutocompleteChange = (_: unknown, newValue: StoreOption[]) => {
    let storeNumbersToSelect = Array.from(new Set((newValue || []).map(store => store.storeNumber)));
    if (maxSelections && storeNumbersToSelect.length > maxSelections) {
      storeNumbersToSelect = storeNumbersToSelect.slice(-maxSelections);
    }

    onChange(
      !hideAllStoresOption && storeNumbers.length > 0 && storeNumbersToSelect.length === storeNumbers.length
        ? { mode: 'all' }
        : { mode: 'specific', storeNumbers: storeNumbersToSelect },
    );
  };

  const baseInputHeight = size === 'small' ? 40 : 56;
  const outlinedPaddingY = size === 'small' ? 6 : 9;
  const tagMarginY = 1;
  const borderWidth = 2;
  const chipHeight = baseInputHeight - (outlinedPaddingY * 2) - (tagMarginY * 2) - borderWidth;
  const chipRowHeight = chipHeight + (tagMarginY * 2);
  const chipsMaxHeight = chipRowHeight * 5;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: allStoresGap, minWidth: 0, width: '100%' }}>
      <Autocomplete<StoreOption, true, false, false>
        multiple
        disableCloseOnSelect
        size={size}
        disabled={disabled}
        options={storeOptions}
        getOptionLabel={option => option.storeName}
        isOptionEqualToValue={(option, selected) => option.storeNumber === selected.storeNumber}
        value={selectedStoreChips}
        onChange={handleStoreAutocompleteChange}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        sx={{
          flex: selectorWidth ? `0 1 ${selectorWidth}` : 1,
          maxWidth: selectorWidth,
          width: selectorWidth,
          minWidth: 0,
          '& .MuiOutlinedInput-root': { minHeight: baseInputHeight, flexWrap: 'wrap', alignItems: 'center' },
          '& .MuiAutocomplete-tag': { marginTop: `${tagMarginY}px`, marginBottom: `${tagMarginY}px` },
          '& .MuiAutocomplete-input': { minWidth: '30px' },
        }}
        renderTags={(tagValue, getTagProps) => (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            maxWidth: '100%',
            maxHeight: `${isOpen ? chipsMaxHeight : chipRowHeight}px`,
            overflowY: isOpen ? 'auto' : 'hidden',
          }}>
            {(isOpen ? tagValue : tagValue.slice(0, 1)).map((option, index) => {
              const { key: ignoredKey, ...chipProps } = getTagProps({ index });
              void ignoredKey;
              return (
                <Chip
                  key={option.storeNumber || index}
                  {...chipProps}
                  size={size}
                  label={option.storeName}
                  sx={{ height: `${chipHeight}px`, fontSize: '0.875rem' }}
                />
              );
            })}
            {!isOpen && tagValue.length > 1 && (
              <Chip
                label={`+${tagValue.length - 1}`}
                size={size}
                sx={{ height: `${chipHeight}px`, fontSize: '0.875rem' }}
              />
            )}
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.storeNumber}
          >
            <Checkbox
              size="small"
              checked={allStoresSelected || (value.mode === 'specific' && value.storeNumbers.includes(option.storeNumber))}
              tabIndex={-1}
              disableRipple
              style={{ marginRight: 8 }}
            />
            {option.storeName}
          </li>
        )}
      />
      {!hideAllStoresOption && (
        <FormControlLabel
          control={(
            <Checkbox
              checked={allStoresSelected}
              onChange={event => onChange(event.target.checked ? { mode: 'all' } : { mode: 'specific', storeNumbers: [] })}
              disabled={disabled}
              size="medium"
            />
          )}
          label={allStoresLabel || 'All Stores'}
          sx={{ flexShrink: 0, mr: 0, whiteSpace: 'nowrap' }}
        />
      )}
    </Box>
  );
};

export default MultiStorePicker;
